import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getLanguageCode} from 'modules/meta/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {HighlightedTitleAccent} from '../HighlightedTitle'
import {getTileImageUrl} from './utils'

import type {HomeFeatureTile} from '@vio/core/src/brand/capabilities/types/domains'
import type {Brand, BrandMessage} from '@vio/core/src/brand/types'

const Container = styled.div`
  background: linear-gradient(
    to top,
    ${cssTheme.colors.background.neutral.primary} 50%,
    #f0ecf5 50%
  );
  position: relative;
  padding-top: 80px;
  border-bottom-right-radius: calc(${cssTheme.layout.radius.xxl} * 2);
  border-bottom-left-radius: calc(${cssTheme.layout.radius.xxl} * 2);
  z-index: 1;

  ${linariaMq.desktopXs} {
    margin-bottom: 130px;
  }
`

const SectionWrapper = styled.div`
  background: ${cssTheme.colors.background.neutral.primary};
  border-radius: calc(${cssTheme.layout.radius.xxl} * 2);
  position: relative;

  padding: ${cssTheme.layout.spacing.s800} ${cssTheme.layout.spacing.s600};

  ${linariaMq.desktopXs} {
    padding: ${cssTheme.layout.spacing.s800} ${cssTheme.layout.spacing.s950};
  }

  ${linariaMq.desktopMd} {
    margin-left: calc(${cssTheme.layout.spacing.s950} * -1);
    margin-right: calc(${cssTheme.layout.spacing.s950} * -1);
  }

  &::before {
    content: '';
    opacity: 0.8;
    border: 1px solid ${cssTheme.colors.border.neutral.secondary};
    background: ${cssTheme.colors.border.brand.secondary};
    -webkit-filter: blur(32px);
    filter: blur(32px);
    top: 10px;
    bottom: 50%;
    right: 15px;
    left: 15px;
    position: absolute;
    z-index: -1;
  }
`

const Title = styled(Text)`
  margin-bottom: ${cssTheme.layout.spacing.s500};

  ${linariaMq.desktopXs} {
    margin-top: ${cssTheme.layout.spacing.s800};
  }
`

const Subtitle = styled(Text)`
  margin-bottom: ${cssTheme.layout.spacing.s950};
`

const BasePowerFeaturesWrapper = styled.div`
  display: flex;
  gap: ${cssTheme.layout.spacing.s700};
  flex-direction: column;
  overflow: hidden;

  ${linariaMq.desktopSm} {
    flex-direction: row;
  }

  h3 {
    margin-bottom: ${cssTheme.layout.spacing.s600};
  }

  > div {
    flex: 1 0 0;
  }
`

const TopPowerFeaturesWrapper = styled(BasePowerFeaturesWrapper)`
  > div {
    .image {
      width: 100%;
      height: 290px;
    }
  }

  > div:nth-child(2) {
    padding-right: 0;
    padding-bottom: 0;

    .image {
      background-size: cover;
    }
  }

  ${linariaMq.mobileLg} {
    > div:nth-child(2) {
      .image {
        background-size: contain;
        background-position: bottom;
      }
    }
  }
`

const PowerFeaturesWrapper = styled(BasePowerFeaturesWrapper)`
  margin-top: ${cssTheme.layout.spacing.s700};

  > div {
    .image {
      width: 100%;
      height: 234px;
    }
  }

  > div:nth-child(3) {
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 0;

    .image {
      background-size: 350px;
      background-position: left top;
    }
  }

  ${linariaMq.mobileLg} {
    > div:nth-child(3) {
      .image {
        background-position: center top !important;
      }
    }
  }

  ${linariaMq.desktopSm} {
    > div:nth-child(3) {
      .image {
        background-position: left top !important;
      }
    }
  }
`

const GridPowerFeaturesWrapper = styled(BasePowerFeaturesWrapper)`
  margin-top: ${cssTheme.layout.spacing.s700};

  > div {
    .image {
      width: 100%;
      height: 290px;
    }
  }
`

const Feature = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: calc(${cssTheme.layout.radius.xl} * 2);
  padding: ${cssTheme.layout.spacing.s700} ${cssTheme.layout.spacing.s600};
  background: ${cssTheme.colors.background.neutral.secondary};
`

const FeatureImage = styled.div<{src: string}>`
  background-image: ${({src}) => `url(${src})`};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: bottom;
  margin: 0 auto;
`

const getTileTitles = (
  messages: Brand['messages']
): Record<HomeFeatureTile, BrandMessage> => ({
  dealFreeze: messages.homeFeatureDealFreezeMessage,
  combineRooms: messages.homeFeatureCombineRoomsMessage,
  payLater: messages.homeFeaturePayLaterMessage,
  priceAlerts: messages.homeFeaturePriceAlertsMessage,
  priceCalendar: messages.homeFeaturePriceCalendarMessage
})

export const PowerFeaturesSection = () => {
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)
  const {brand} = useBrand()
  const {
    shell: {homeFeatureTiles, homeFeatureTileImagesPath}
  } = useBrandCapabilities()
  const tileTitles = getTileTitles(brand.messages)
  const topTiles = homeFeatureTiles.slice(0, 2)
  const bottomTiles = homeFeatureTiles.slice(2)
  const BottomWrapper =
    homeFeatureTiles.length === 4
      ? GridPowerFeaturesWrapper
      : PowerFeaturesWrapper

  const renderTile = (tile: HomeFeatureTile) => (
    <Feature key={tile}>
      <Text align="center" variant="titleL">
        {formatMessage(tileTitles[tile], {br: () => <br />})}
      </Text>
      <FeatureImage
        className="image"
        src={getTileImageUrl(homeFeatureTileImagesPath, languageCode, tile)}
      />
    </Feature>
  )

  return (
    <Container>
      <PageWidthContainer>
        <SectionWrapper>
          <Title align="center" variant={isMobile ? 'title2XL' : 'title4XL'}>
            <FormattedMessageWrapper
              {...brand.messages.homeFeaturesTitleMessage}
              values={{
                span: chunks => (
                  <HighlightedTitleAccent>{chunks}</HighlightedTitleAccent>
                )
              }}
            />
          </Title>
          <Subtitle align="center" variant="bodyL">
            <FormattedMessageWrapper
              {...brand.messages.homeFeaturesSubtitleMessage}
            />
          </Subtitle>

          <TopPowerFeaturesWrapper>
            {topTiles.map(renderTile)}
          </TopPowerFeaturesWrapper>

          <BottomWrapper>{bottomTiles.map(renderTile)}</BottomWrapper>
        </SectionWrapper>
      </PageWidthContainer>
    </Container>
  )
}
